import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../services/api';
import { fetchSystemDayYmd } from '../utils/recentDealsDate';
import DealSlipViewModal from '../components/DealSlipViewModal';
import { SLIP_DEAL_TYPE, toSlipTransaction } from '../utils/dealSlip';
import './DailyDealBlotter.css';

const DEAL_TYPE_LABELS = {
  TRANSACTION: 'Transaction',
  MONEY_MARKET: 'Money Market',
  GSEC: 'GSec',
  TBILL: 'T-Bill',
  BUYBACK: 'Buyback',
  FIXED_DEPOSIT: 'Fixed Deposit',
  REPO: 'Repo'
};

const DEAL_TYPE_CHIP_CLASS = {
  BUYBACK: 'ddb-chip--buyback',
  REPO: 'ddb-chip--repo',
  GSEC: 'ddb-chip--gsec',
  TBILL: 'ddb-chip--tbill',
  MONEY_MARKET: 'ddb-chip--money_market'
};

const STATUS_CLASS = {
  Approved: 'ddb-status--ok',
  Rejected: 'ddb-status--rejected',
  Draft: 'ddb-status--draft',
  'Pending Front Office': 'ddb-status--pending',
  'Pending Back Office Verification': 'ddb-status--pending',
  'Pending Final Approval': 'ddb-status--pending'
};

const isPending = (status) => STATUS_CLASS[status] === 'ddb-status--pending';

const StatusLabel = ({ status }) => (
  <span className={`ddb-status ${STATUS_CLASS[status] || ''}`.trim()}>
    {isPending(status) && <span className="ddb-dot" />}
    {status || '—'}
  </span>
);

const DealTypeChip = ({ dealType }) => (
  <span className={`ddb-chip ${DEAL_TYPE_CHIP_CLASS[dealType] || ''}`.trim()}>
    {DEAL_TYPE_LABELS[dealType] || dealType}
  </span>
);

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB');
  } catch {
    return dateStr;
  }
};

// Numbers stay numbers in the sheet so they can be summed; blanks stay blank.
const toNumberOrBlank = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? n : value;
};

const DailyDealBlotter = () => {
  const [date, setDate] = useState('');
  const [dealTypeFilter, setDealTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState({ totalDeals: 0, byDealType: {}, byStatus: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);
  const [viewLoadingKey, setViewLoadingKey] = useState('');

  const openView = async (deal, key) => {
    setViewLoadingKey(key);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/daily-deal-blotter/deal`, {
        params: { dealType: deal.dealType, dealNumber: deal.dealNumber }
      });
      setViewing({
        dealType: SLIP_DEAL_TYPE[deal.dealType],
        transaction: toSlipTransaction(deal.dealType, response.data.data, deal.counterparty)
      });
    } catch (err) {
      setError(err.response?.data?.error || `Could not load deal ${deal.dealNumber}`);
    } finally {
      setViewLoadingKey('');
    }
  };

  const fetchBlotter = useCallback(async (selectedDate) => {
    if (!selectedDate) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/daily-deal-blotter`, {
        params: { date: selectedDate }
      });
      if (response.data.success) {
        setDeals(response.data.data || []);
        setSummary(response.data.summary || { totalDeals: 0, byDealType: {}, byStatus: {} });
      } else {
        setError(response.data.error || 'Failed to fetch daily deal blotter');
        setDeals([]);
      }
    } catch (err) {
      console.error('Error fetching daily deal blotter:', err);
      setError(err.response?.data?.error || 'Failed to fetch daily deal blotter');
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const systemDay = await fetchSystemDayYmd();
      if (mounted) {
        setDate(systemDay);
        fetchBlotter(systemDay);
      }
    })();
    return () => { mounted = false; };
  }, [fetchBlotter]);

  const dealTypeOptions = useMemo(() => Object.keys(DEAL_TYPE_LABELS), []);
  const statusOptions = useMemo(() => Object.keys(STATUS_CLASS), []);

  const filteredDeals = useMemo(() => (
    deals.filter((d) =>
      (!dealTypeFilter || d.dealType === dealTypeFilter) &&
      (!statusFilter || d.status === statusFilter)
    )
  ), [deals, dealTypeFilter, statusFilter]);

  const pendingCount = useMemo(() => deals.filter((d) => isPending(d.status)).length, [deals]);

  const handleRefresh = () => fetchBlotter(date);

  // Exports exactly what is on screen: the loaded date with the current filters applied.
  const exportToExcel = () => {
    const rows = filteredDeals.map((d) => ({
      'Deal Type': DEAL_TYPE_LABELS[d.dealType] || d.dealType,
      'Deal Number': d.dealNumber || '',
      'Deal Date': d.dealDate ? formatDate(d.dealDate) : '',
      'Value Date': d.valueDate ? formatDate(d.valueDate) : '',
      Counterparty: d.counterparty || '',
      Instrument: d.instrument || '',
      'Face Value': toNumberOrBlank(d.amount),
      'Settlement Value': toNumberOrBlank(d.settlementValue),
      Rate: toNumberOrBlank(d.rate),
      Status: d.status || '',
      'Entered By': d.enteredBy || '',
      'FO Auth': d.frontOfficeBy || '',
      'BO Auth': d.backOfficeVerifierBy || '',
      'Final Auth': d.finalApprovedBy || ''
    }));

    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet['!cols'] = [
      { wch: 14 }, { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 32 }, { wch: 28 },
      { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 32 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }
    ];
    const range = XLSX.utils.decode_range(sheet['!ref']);
    for (let r = 1; r <= range.e.r; r += 1) {
      [6, 7].forEach((c) => {
        const cell = sheet[XLSX.utils.encode_cell({ r, c })];
        if (cell && cell.t === 'n') cell.z = '#,##0.00';
      });
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Daily Deal Blotter');
    const typePart = dealTypeFilter ? `_${dealTypeFilter}` : '';
    const statusPart = statusFilter ? `_${statusFilter.replace(/\s+/g, '-')}` : '';
    XLSX.writeFile(workbook, `Daily_Deal_Blotter_${date}${typePart}${statusPart}.xlsx`);
  };

  return (
    <div className="ddb-page">
      <header className="ddb-header">
        <div>
          <h1>Daily Deal Blotter</h1>
          <p className="ddb-sub">Every deal entered on one deal date, across all desks.</p>
        </div>
        <div className="ddb-controls">
          <label className="ddb-field">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="ddb-field">
            <span>Deal Type</span>
            <select value={dealTypeFilter} onChange={(e) => setDealTypeFilter(e.target.value)}>
              <option value="">All Deal Types</option>
              {dealTypeOptions.map((dt) => (
                <option key={dt} value={dt}>{DEAL_TYPE_LABELS[dt]}</option>
              ))}
            </select>
          </label>
          <label className="ddb-field">
            <span>Status</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <button type="button" className="ddb-btn" onClick={handleRefresh} disabled={loading || !date}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="ddb-btn ddb-btn--export"
            onClick={exportToExcel}
            disabled={loading || filteredDeals.length === 0}
          >
            Export to Excel
          </button>
        </div>
      </header>

      {error && <div className="ddb-alert ddb-alert--error" role="alert">{error}</div>}

      {!loading && pendingCount > 0 && (
        <div className="ddb-alert ddb-alert--pending">
          <span className="ddb-spinner" aria-hidden="true" />
          <div>
            <strong>
              {pendingCount} deal{pendingCount === 1 ? '' : 's'} still awaiting approval.
            </strong>
            <div>These are marked with a pulsing dot in the Status column.</div>
          </div>
        </div>
      )}

      <section className="ddb-statement">
        {!loading && deals.length > 0 && (
          <div className="ddb-band">
            <div className="ddb-band-label">
              Total Deals
              <span className="ddb-band-note">{formatDate(date)}</span>
            </div>
            <div className="ddb-band-chips">
              {Object.entries(summary.byDealType || {}).map(([type, count]) => (
                <span key={type} className="ddb-summary-chip">
                  {DEAL_TYPE_LABELS[type] || type} <strong>{count}</strong>
                </span>
              ))}
            </div>
            <div className="ddb-band-amount">{summary.totalDeals}</div>
          </div>
        )}

        <div className="ddb-table-wrap">
          <table className="ddb-table">
            <thead>
              <tr>
                <th>Deal Type</th>
                <th>Deal Number</th>
                <th>Deal Date</th>
                <th>Value Date</th>
                <th>Counterparty</th>
                <th>Instrument</th>
                <th className="ddb-num">Face Value</th>
                <th className="ddb-num">Settlement Value</th>
                <th className="ddb-num">Rate</th>
                <th>Status</th>
                <th>Entered By</th>
                <th>FO Auth</th>
                <th>BO Auth</th>
                <th>Final Auth</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {!loading && filteredDeals.map((deal, idx) => {
                const rowKey = `${deal.dealType}-${deal.dealNumber}-${idx}`;
                return (
                  <tr key={rowKey} className={isPending(deal.status) ? 'ddb-row--pending' : ''}>
                    <td><DealTypeChip dealType={deal.dealType} /></td>
                    <td className="ddb-mono">{deal.dealNumber || '—'}</td>
                    <td className="ddb-mono">{formatDate(deal.dealDate)}</td>
                    <td className="ddb-mono">{formatDate(deal.valueDate)}</td>
                    <td>{deal.counterparty || '—'}</td>
                    <td className="ddb-desc">{deal.instrument || '—'}</td>
                    <td className="ddb-num">{formatAmount(deal.amount)}</td>
                    <td className="ddb-num">{formatAmount(deal.settlementValue)}</td>
                    <td className="ddb-num">
                      {deal.rate !== null && deal.rate !== undefined ? deal.rate : '—'}
                    </td>
                    <td><StatusLabel status={deal.status} /></td>
                    <td>{deal.enteredBy || '—'}</td>
                    <td>{deal.frontOfficeBy || '—'}</td>
                    <td>{deal.backOfficeVerifierBy || '—'}</td>
                    <td>{deal.finalApprovedBy || '—'}</td>
                    <td className="ddb-view-cell">
                      {SLIP_DEAL_TYPE[deal.dealType] && deal.dealNumber ? (
                        <button
                          type="button"
                          className="ddb-btn ddb-btn--sm ddb-btn--view"
                          onClick={() => openView(deal, rowKey)}
                          disabled={viewLoadingKey === rowKey}
                        >
                          {viewLoadingKey === rowKey ? 'Loading…' : 'View'}
                        </button>
                      ) : (
                        <span className="ddb-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!loading && filteredDeals.length === 0 && (
                <tr>
                  <td colSpan={15} className="ddb-empty">
                    {date ? (
                      <>
                        No deals found for {formatDate(date)}
                        {dealTypeFilter ? ` (${DEAL_TYPE_LABELS[dealTypeFilter]})` : ''}
                        {statusFilter ? ` — ${statusFilter}` : ''}.
                      </>
                    ) : 'Select a date.'}
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan={15} className="ddb-empty">Loading…</td>
                </tr>
              )}
            </tbody>
            {!loading && filteredDeals.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={15}>
                    {filteredDeals.length} of {deals.length} deal{deals.length === 1 ? '' : 's'} shown
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      {viewing && (
        <DealSlipViewModal
          dealType={viewing.dealType}
          transaction={viewing.transaction}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
};

export default DailyDealBlotter;
